import moment from 'moment';
import _ from 'lodash';
import i18n from 'i18next';
import en from '../locales/en';
import ko from '../locales/ko';

const selectTranslation = (lang) => {
  return {
    en: en,
    ko: ko,
  }[lang];
};

export const dateFormatter = (date) => {
  return moment(date).format('YYYY-MM-DD HH:mm');
};

export const reportDateFormatter = (date) => {
  return moment(date).format('YYYY-MM-DD');
};

export const reportHourFormatter = (date) => {
  return moment(date).format('HH:mm');
};

export const bookingDateFormatter = (date) => {
  const formattedDate = moment(date).format('YYYY.M.D');
  const formattedDay = moment(date).format('ddd');
  return `${formattedDate} (${korDayFormatter(formattedDay.toLowerCase())})`;
};

const korDayFormatter = (day) => {
  const formatter = {
    sun: '일',
    mon: '월',
    tue: '화',
    wed: '수',
    thu: '목',
    fri: '금',
    sat: '토',
  };
  return formatter[day];
};

export const doorlockTypeFormatter = (type) => {
  const translation = selectTranslation(i18n.language);
  if (!translation) return ;
  if (_.isEmpty(type)) return '-';

  return {
    commonroom: translation.data.doorlock_type.commonroom,
    room: translation.data.doorlock_type.room,
    building: translation.data.doorlock_type.building,
    floor: translation.data.doorlock_type.floor,
    elevator: translation.data.doorlock_type.elevator,
    keytag: translation.data.doorlock_type.keytag,
  }[type] || 'Unknown';
};

export const doorlockStatusFormatter = (status) => {
  const translation = selectTranslation(i18n.language);
  if (!translation) return ;
  if (_.isEmpty(status)) return '-';

  return {
    created: translation.data.doorlock_status.created,
    installed: translation.data.doorlock_status.installed,
  }[status] || 'UnKnown';
};

export const doorlockBatteryFormatter = (battery) => {
  return `${battery}%`;
};

export const staffRoleFormatter = (role) => {
  const translation = selectTranslation(i18n.language);
  if (!translation) return ;
  if (_.isEmpty(role)) return '-';

  return {
    master: translation.data.staff_role.master,
    manager: translation.data.staff_role.manager,
    housekeeping: translation.data.staff_role.housekeeping,
    manager_mobilekey: translation.data.staff_role.manager_mobilekey,
    doorlock_setting: translation.data.staff_role.doorlock_setting,
  }[role] || 'UnKnown';
};

export const staffStatusFormatter = (status) => {
  const translation = selectTranslation(i18n.language);
  if (!translation) return ;
  if (_.isEmpty(status)) return '-';

  return {
    granting: translation.data.regist_status.granting,
    granted: translation.data.regist_status.granted,
  }[status] || 'UnKnown';
};

export const roomsFormatter = (rooms) => {
  return JSON.stringify(rooms.map(room => room.name)).replace(/["[\]]/gi, '');
};

export const reportDataExcelColumns = (reportType) => {
  const translation = selectTranslation(i18n.language);
  if (!translation) return ;

  return {
    userkey: [
      { name: translation.data_column.key_type, key: 'key_type' },
      { name: translation.data_column.content, key: 'operation' },
      { name: translation.data_column.building, key: 'building_name' },
      { name: translation.data_column.room, key: 'json_destination.room' },
      { name: translation.data_column.user_name, key: 'grantee_name' },
      { name: translation.data_column.phone_number, key: 'grantee_phone_number' },
      { name: translation.data_column.checkin_day, key: 'start_at_day' },
      { name: translation.data_column.checkin_hour, key: 'start_at_hour' },
      { name: translation.data_column.checkout_day, key: 'end_at_day' },
      { name: translation.data_column.checkout_hour, key: 'end_at_hour' },
      { name: translation.data_column.staff, key: 'actor_name' },
      { name: translation.data_column.sequence, key: 'json_context.seq_num' },
      { name: translation.data_column.created_at, key: 'created_at' },
      { name: translation.data_column.pincode, key: 'json_context.pincode' },
      { name: translation.data_column.exchangekey, key: 'json_context.exchangekey' },
    ],
    facility: [
      { name: translation.data_column.facility, key: 'facility_name' },
      { name: translation.data_column.building, key: 'building_name' },
      { name: translation.data_column.content, key: 'operation' },
      { name: translation.data_column.content_detail, key: 'json_diffs' },
      { name: translation.data_column.staff, key: 'actor_name' },
      { name: translation.data_column.created_at, key: 'created_at' },
    ],
    staff: [
      { name: translation.data_column.actor_name, key: 'actor_name' },
      { name: translation.data_column.actor_phone_number, key: 'actor_phone_number' },
      { name: translation.data_column.actor_role, key: 'actor_role' },
      { name: translation.data_column.content, key: 'operation' },
      { name: translation.data_column.key_type, key: 'key_type' },
      { name: translation.data_column.content_detail, key: 'json_diffs' },
      { name: translation.data_column.room, key: 'json_destination.rooms' },
      { name: translation.data_column.owner_name, key: 'grantee_name' },
      { name: translation.data_column.owner_phone_number, key: 'grantee_phone_number' },
      { name: translation.data_column.owner_role, key: 'grantee_role' },
      { name: translation.data_column.created_at, key: 'created_at' },
    ],
    user_mobilekey: [
      { name: translation.data_column.building, key: 'building_name' },
      { name: translation.data_column.room, key: 'json_destination.room' },
      { name: translation.data_column.content, key: 'operation' },
      { name: translation.data_column.key_user_name, key: 'actor_name' },
      { name: translation.data_column.phone_number, key: 'actor_phone_number' },
      { name: translation.data_column.created_at, key: 'created_at' },
    ],
  }[reportType] || [];
};

export const reportListFormatter = (reportItem, key) => {
  switch (key) {
    case 'operation':
      return reportContentFormatter(reportItem.operation);
    case 'key_type':
      return reportKeyTypeFormatter(reportItem.key_type);
    case 'actor_role':
      return staffRoleFormatter(reportItem.actor_role);
    case 'grantee_role':
      return staffRoleFormatter(reportItem.grantee_role);
    case 'start_at_day':
      return reportItem.json_context ?
        reportDateFormatter(reportItem.json_context.start_at) : '-';
    case 'start_at_hour':
      return reportItem.json_context ?
        reportHourFormatter(reportItem.json_context.start_at) : '-';
    case 'end_at_day':
      return reportItem.json_context ?
        reportDateFormatter(reportItem.json_context.end_at) : '-';
    case 'end_at_hour':
      return reportItem.json_context ?
        reportHourFormatter(reportItem.json_context.end_at) : '-';
    case 'json_destination.room':
      return reportItem.json_destination ? reportItem.json_destination.room : '-';
    case 'json_destination.rooms':
      return reportItem.json_destination ? reportItem.json_destination.rooms : '-';
    case 'json_context.seq_num':
      return reportItem.json_context ? reportItem.json_context.seq_num : '-';
    case 'json_diffs':
      return reportItem.json_diffs ? reportJsonDiffsFormatter(reportItem.json_diffs) : '-';
    case 'created_at':
      return dateFormatter(reportItem.created_at);
    case 'json_context.pincode':
      return reportItem.json_context ? reportItem.json_context?.pincode : '-';
    case 'json_context.exchangekey':
      return reportItem.json_context ? reportItem.json_context?.exchangekey : '-';
    default:
      return reportItem[key] || '-';
  }
};

export const reportContentFormatter = (operation) => {
  const translation = selectTranslation(i18n.language);
  if (!translation) return ;
  if (_.isEmpty(operation)) return '-';

  return {
    'Issue UserKey': translation.data.report_content.issue_userkey,
    'Cancel UserKey': translation.data.report_content.cancel_userkey,
    'Create Hotel': translation.data.report_content.create_hotel,
    'Update Hotel': translation.data.report_content.update_hotel,
    'Delete Hotel': translation.data.report_content.delete_hotel,
    'Create Building': translation.data.report_content.create_building,
    'Update Building': translation.data.report_content.update_building,
    'Delete Building': translation.data.report_content.delete_building,
    'Create Room': translation.data.report_content.create_room,
    'Update Room': translation.data.report_content.update_room,
    'Delete Room': translation.data.report_content.delete_room,
    'Create Commonroom': translation.data.report_content.create_commonroom,
    'Update Commonroom': translation.data.report_content.update_commonroom,
    'Delete Commonroom': translation.data.report_content.delete_commonroom,
    'Install Doorlock': translation.data.report_content.install_doorlock,
    'Uninstall Doorlock': translation.data.report_content.uninstall_doorlock,
    'Grant Staff': translation.data.report_content.grant_staff,
    'Update Staff': translation.data.report_content.update_staff,
    'Revoke Staff': translation.data.report_content.revoke_staff,
    'Issue StaffKey': translation.data.report_content.issue_staffkey,
    'Cancel StaffKey': translation.data.report_content.cancel_staffkey,
    'Start Cleaning': translation.data.report_content.start_cleaning,
    'End Cleaning': translation.data.report_content.end_cleaning,
    'Open StaffMobileKey': translation.data.report_content.open_staffmobilekey,
    'Open UserMobileKey': translation.data.report_content.open_usermobilekey,
  }[operation] || 'UnKnown';
};

export const reportKeyTypeFormatter = (keyType) => {
  const translation = selectTranslation(i18n.language);
  if (!translation) return ;
  if (_.isEmpty(keyType)) return '-';

  return {
    mobilekey: translation.data.report_key_type.mobilekey,
    pincode: translation.data.report_key_type.pincode,
    cardkey: translation.data.report_key_type.cardkey,
    cardkey_master: translation.data.report_key_type.cardkey_master,
    cardkey_ble: translation.data.report_key_type.cardkey_ble,
    cardkey_init: translation.data.report_key_type.cardkey_init,
  }[keyType] || 'UnKnown';
};

const reportJsonDiffsFormatter = (diffs) => {
  return diffs.map((diff) => diff.key + ': ' + diff.oldValue + ' -> ' + diff.newValue);
};

export const pincodeTypeFormatter = (pincodeType) => {
  const translation = selectTranslation(i18n.language);
  if (!translation) return ;
  if (_.isEmpty(pincodeType)) return '-';

  return {
    day: translation.data.pincode_type.day,
    hour: translation.data.pincode_type.hour,
    '10mins': translation.data.pincode_type.min,
  }[pincodeType] || 'UnKnown';
};

export const languageFormatter = (language) => {
  return {
    ko: '한국어',
    en: 'English',
  }[language] || 'Unknown';
};